import React, { useState, useEffect } from 'react';
import { ExclamationCircleIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { PublicClientApplication } from '@azure/msal-browser';

const LoginComponent = ({ onLogin, error, isLoading }) => {
  const [msalInstance, setMsalInstance] = useState(null);
  const [authConfig, setAuthConfig] = useState(null);

  useEffect(() => {
    // Fetch auth configuration from backend
    const fetchAuthConfig = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://everfield-gpt-v2-backend.politecoast-05366af5.swedencentral.azurecontainerapps.io';
        const response = await fetch(`${apiUrl}/api/auth/config`);
        const config = await response.json();
        setAuthConfig(config);

        // Initialize MSAL instance
        const msalConfig = {
          auth: {
            clientId: config.clientId,
            authority: config.authority,
            redirectUri: config.redirectUri
          },
          cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false
          }
        };

        const msal = new PublicClientApplication(msalConfig);
        await msal.initialize();
        setMsalInstance(msal);
      } catch (error) {
        console.error('Failed to fetch auth config:', error);
      }
    };

    fetchAuthConfig();
  }, []);

  const handleSignIn = async () => {
    if (!msalInstance || !authConfig || isLoading) return;

    try {
      const loginResponse = await msalInstance.loginPopup({
        scopes: authConfig.scopes,
        prompt: "select_account"
      });

      // Send the access token to your backend
      onLogin(loginResponse.accessToken);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50
                    dark:from-[#0f0f23] dark:via-[#16161e] dark:to-[#1a1b26]
                    flex items-center justify-center p-4">

      {/* Background Effects */}
      <div className="mesh-gradient fixed inset-0 pointer-events-none z-0" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="w-32 h-32 mx-auto mb-6 rounded-3xl shadow-2xl border-4 border-white/20 dark:border-gray-700/50"
               style={{ background: '#03101f' }}>
            <img src="/everfield-logo.png" alt="Everfield Logo" className="w-full h-full object-contain rounded-2xl" />
          </div>
          <h1 className="text-4xl font-bold bg-clip-text text-transparent
                       bg-gradient-to-r from-blue-600 via-purple-600 to-blue-400
                       dark:from-[#7aa2f7] dark:via-[#bb9af7] dark:to-[#7dcfff] font-mono mb-2">
            EverfieldGPT
          </h1>
          <p className="text-gray-600 dark:text-[#9aa5ce] text-lg">
            Sign in with your Everfield account
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white/95 dark:bg-[#1a1b26]/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-[#414868]/50 p-8">
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-[#f7768e]/20
                            text-red-600 dark:text-[#f7768e] border border-red-100 dark:border-[#f7768e]/30">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Microsoft Sign In Button */}
            <button
              onClick={handleSignIn}
              disabled={isLoading || !msalInstance}
              className={`w-full py-4 px-6 rounded-2xl text-white font-semibold text-lg
                       transition-all duration-300 ease-out transform flex items-center justify-center space-x-3
                       ${isLoading || !msalInstance
                         ? 'bg-gray-400 dark:bg-[#565f89] cursor-not-allowed scale-95 opacity-50'
                         : 'bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 hover:from-blue-600 hover:via-purple-600 hover:to-blue-700 hover:scale-[1.02] shadow-xl hover:shadow-2xl'
                       }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  <span>Sign in with Microsoft</span>
                </>
              )}
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 dark:text-[#9aa5ce]">
              Secure enterprise authentication
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500 dark:text-[#9aa5ce]">
            © {new Date().getFullYear()} Everfield HQ. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginComponent;